import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { WindowService } from "@merri/common";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppThemeColors, LoggingService } from "src/app/core";
import { VisionboardItem } from "../../../models";
import { VisionboardItemAddRemoveUiServiceBase, VisionboardWorkspaceItemsStoreService } from "../../../services";
import { VisionboardItemAddRemoveButtonService } from "../../services";

export enum ForceVisionboardItemAddRemoveButton {
  Add = "add",
  Remove = "remove",
  None = "none",
}

@Component({
    selector: "app-visionboard-item-add-remove-button",
    templateUrl: "./visionboard-item-add-remove-button.component.html",
    styleUrls: ["./visionboard-item-add-remove-button.component.scss"],
    standalone: false
})
export class VisionboardItemAddRemoveButtonComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject();

  @Input() item: VisionboardItem;
  @Input() forceVisionboardItemAddRemoveButton: ForceVisionboardItemAddRemoveButton;
  @Input() compactMode: boolean = true;
  @Input() source: string;

  @Output() itemAdded: EventEmitter<void> = new EventEmitter<void>();
  @Output() itemRemoved: EventEmitter<void> = new EventEmitter<void>();

  showAddButton: boolean;
  showRemoveButton: boolean;
  mobileMode: boolean;
  removeButtonTooltip: string;
  removeButtonLargeText: string;
  addButtonTooltip: string;
  addButtonLargeText: string;
  buttonColor: AppThemeColors;

  @HostBinding("style.display") public display = "none";

  constructor(
    private _visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private _visionboardItemAddRemoveButtonService: VisionboardItemAddRemoveButtonService,
    private _visionboardItemAddRemoveUiService: VisionboardItemAddRemoveUiServiceBase,
    private _windowService: WindowService,
    private _loggingService: LoggingService,
  ) {}

  ngOnInit(): void {
    this.setState();

    this._visionboardWorkspaceItemsStoreService.items$.pipe(takeUntil(this._destroyed$)).subscribe({ next: () => this.setState() });

    this._windowService
      .observeMax$(539)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: m => {
          this.mobileMode = m.matches;
        },
      });

    this.removeButtonTooltip = this._visionboardItemAddRemoveUiService.removeButtonTooltip;
    this.removeButtonLargeText = this._visionboardItemAddRemoveUiService.removeButtonLargeText;
    this.addButtonTooltip = this._visionboardItemAddRemoveUiService.addButtonTooltip;
    this.addButtonLargeText = this._visionboardItemAddRemoveUiService.addButtonLargeText;
    this.buttonColor = this._visionboardItemAddRemoveUiService.color;

    if (this.compactMode === undefined) {
      this.compactMode = true;
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onRemoveClick($event: MouseEvent): void {
    $event.stopPropagation();

    this._loggingService.logRemoteInfo("Visionboard Add/Remove Item", "VisionboardItemAddRemoveButtonComponent.onRemoveClick", {
      Workflow: "VisionBoard Interaction",
      WorkflowStep: "Remove Item",
      WorkflowSource: this.source,
      VariantSkuId: `${this.item.variantSkuId}`,
    });

    if (this.itemRemoved.observed) {
      this.itemRemoved.emit();
      return;
    }

    this._visionboardItemAddRemoveButtonService.removeItemClick({
      item: this.item,
    });
  }

  onAddClick($event: MouseEvent): void {
    $event.stopPropagation();

    this._loggingService.logRemoteInfo("Visionboard Add/Remove Item", "VisionboardItemAddRemoveButtonComponent.onAddClick", {
      Workflow: "VisionBoard Interaction",
      WorkflowStep: "Add Item",
      WorkflowSource: this.source,
      VariantSkuId: `${this.item.variantSkuId}`,
    });

    if (this.itemAdded.observed) {
      this.itemAdded.emit();
      return;
    }

    this._visionboardItemAddRemoveButtonService.addItemClick({
      item: this.item,
    });
  }

  setState(): void {
    if (this.forceVisionboardItemAddRemoveButton) {
      this.showAddButton = this.forceVisionboardItemAddRemoveButton === ForceVisionboardItemAddRemoveButton.Add;
      this.showRemoveButton =
        this.forceVisionboardItemAddRemoveButton === ForceVisionboardItemAddRemoveButton.Remove && !this.item.isVenueProvided;

      if (this.forceVisionboardItemAddRemoveButton === ForceVisionboardItemAddRemoveButton.None) {
        this.showAddButton = this.showRemoveButton = false;
      }
    } else {
      this.showRemoveButton = this._visionboardWorkspaceItemsStoreService.userHasItem(this.item.variantSkuId) && !this.item.isVenueProvided;
      this.showAddButton = !this._visionboardWorkspaceItemsStoreService.userHasItem(this.item.variantSkuId);
    }

    if (this.showRemoveButton || this.showAddButton) {
      this.display = "block";
    }
  }
}
